import secureApi from '../../utils/secureApi';
import axios from 'axios';
import { getImageUrl } from '../../utils/imageUrl';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Tournament interface based on the provided API response
export interface Tournament {
  _id: string;
  name: string;
  shortName: string;
  logo: string;
  thumbnail?: string; // Add thumbnail field
  location: string;
  startDate: string;
  endDate: string;
  status: 'upcoming' | 'past' | 'ongoing' | 'completed';
  sports: string;
  ageCategories: string[];
  isRegistrationOpen: boolean;
  isTeamRegistered?: boolean; // New field to indicate if team is registered
}

// API response interface
export interface TournamentApiResponse {
  statusCode: number;
  status: string;
  message: string;
  data: {
    tournaments: Tournament[];
  };
}

// Error interface
export interface ApiError {
  statusCode: number;
  status: string;
  message: string;
}

/**
 * Normalize a logo URL or S3 key to an absolute URL
 */
const normalizeLogoUrl = (raw: any): string => {
  const candidate = raw?.logo || raw?.teamLogo || raw?.tournamentLogo || raw?.logoUrl || '';
  if (!candidate) return '';
  return getImageUrl(candidate);
};

/**
 * Map backend tournament shape to UI Tournament type with safe defaults
 */
const normalizeTournament = (t: any): Tournament => ({
  _id: t._id,
  name: t.name || t.tournamentName || '',
  shortName: t.shortName || t.code || '',
  logo: normalizeLogoUrl(t),
  thumbnail: t.thumbnail || t.coverImage || '', // Add thumbnail field
  location: t.location || t.venue || '',
  startDate: t.startDate || t.start_time || t.date || '',
  endDate: t.endDate || t.end_time || t.date || '',
  status: (t.status || 'upcoming') as Tournament['status'],
  sports: t.sports || t.sport || 'Football',
  ageCategories: Array.isArray(t.ageCategories) ? t.ageCategories : (t.ageCategory ? [t.ageCategory] : []),
  isRegistrationOpen: Boolean(t.isRegistrationOpen ?? t.registrationOpen ?? true),
  isTeamRegistered: Boolean(t.isTeamRegistered ?? t.teamRegistered ?? t.isRegistered ?? false), // Handle multiple possible field names
});

/**
 * Fetch tournaments by status for clubmanager role
 * @param status - Tournament status ('upcoming', 'past', 'ongoing', 'completed')
 * @returns Promise<Tournament[]> - Array of tournaments
 */
export const getTournamentsByStatus = async (status: string): Promise<Tournament[]> => {
  try {
    // secureApi handles token management automatically
    const response = await secureApi.get<TournamentApiResponse>(
      `/api/v1/tournament-registration/list-all-tournaments`,
      {
        params: { status }
      }
    );
    
    // Handle different response structures
    if (response.statusCode === 200 && response.status === 'success') {
      const list = response.data.tournaments || [];
      return list.map((t: any) => normalizeTournament(t));
    } else if ((response as any).tournaments) {
      // Direct tournaments array in response
      return ((response as any).tournaments as any[]).map(normalizeTournament);
    } else if (Array.isArray(response)) {
      // Direct array response
      return (response as any[]).map(normalizeTournament);
    } else {
      console.error('Unexpected response structure:', response);
      throw new Error((response as any).message || 'Failed to fetch tournaments');
    }
  } catch (error: any) {
    console.error(`Failed to fetch ${status} tournaments:`, error);
    console.error('Error response data:', error.response?.data);
    console.error('Error status:', error.response?.status);
    console.error('Request URL:', error.config?.url);
    console.error('Request headers:', error.config?.headers);
    
    // Handle 404 errors for all tournament statuses as "no data available"
    if (error.response?.status === 404) {
      console.log(`No ${status} tournaments found in the system - returning empty array`);
      return [];
    }
    
    // For other errors, throw to be handled by caller
    throw error;
  }
};

/**
 * Fetch upcoming tournaments
 * @returns Promise<Tournament[]> - Array of upcoming tournaments
 */
export const getUpcomingTournaments = async (): Promise<Tournament[]> => {
  return getTournamentsByStatus('upcoming');
};

/**
 * Fetch past tournaments
 * @returns Promise<Tournament[]> - Array of past tournaments
 */
export const getPastTournaments = async (): Promise<Tournament[]> => {
  return getTournamentsByStatus('past');
};

/**
 * Fetch all tournaments (upcoming and past)
 * @returns Promise<{upcoming: Tournament[], past: Tournament[]}>
 */
export const getAllTournaments = async (): Promise<{
  upcoming: Tournament[];
  past: Tournament[];
}> => {
  try {
    const [upcoming, past] = await Promise.all([
      getUpcomingTournaments(),
      getPastTournaments()
    ]);

    return { upcoming, past };
  } catch (error) {
    console.error('Failed to fetch all tournaments:', error);
    throw error;
  }
};

/**
 * Get tournament by ID
 * @param tournamentId - Tournament ID
 * @returns Promise<Tournament> - Tournament details
 */
export const getTournamentById = async (tournamentId: string): Promise<Tournament> => {
  try {
    // secureApi handles token management automatically
    const response = await secureApi.get<TournamentApiResponse>(
      `/api/v1/tournaments/manage/tournament-info/${tournamentId}`
    );

    if (response.statusCode === 200 && response.status === 'success') {
      const t = response.data;
      if (!t) {
        throw new Error('Tournament not found in response data');
      }
      return normalizeTournament(t);
    } else if ((response as any)._id) {
      // Direct tournament data in response (fallback)
      return normalizeTournament(response as any);
    } else {
      throw new Error(response.message || 'Failed to fetch tournament details');
    }
  } catch (error: any) {
    console.error('❌ Failed to fetch tournament details:', error);
    console.error('❌ Error response:', error.response?.data);
    console.error('❌ Error status:', error.response?.status);
    console.error('❌ Request URL:', error.config?.url);
    
    // If it's a 404, provide a more specific error message
    if (error.response?.status === 404) {
      throw new Error(`Tournament with ID ${tournamentId} not found`);
    }
    
    throw error;
  }
};

/**
 * Export tournament teams to Excel
 * @param tournamentId - Tournament ID
 * @returns Promise<Blob> - Excel file blob
 */
// Cache to prevent multiple simultaneous requests
const exportCache = new Map<string, Promise<Blob>>();

// Test function to debug the API endpoint
export const testExportEndpoint = async (tournamentId: string) => {
  try {
    const url = `/api/v1/tournament-registration/excel/detail/${tournamentId}`;
    
    console.log('🧪 Testing export endpoint...');
    console.log('URL:', url);
    
    // Test with different response types using secureApi
    const responses = await Promise.allSettled([
      secureApi.get(url, { responseType: 'json' }),
      secureApi.get(url, { responseType: 'text' }),
      secureApi.get(url, { responseType: 'blob' })
    ]);
    
    responses.forEach((response, index) => {
      const types = ['json', 'text', 'blob'];
      console.log(`\n📊 ${types[index].toUpperCase()} Response:`);
      
      if (response.status === 'fulfilled') {
        const res = response.value;
        console.log('- Status:', res.status);
        console.log('- Headers:', res.headers);
        console.log('- Data type:', typeof res.data);
        console.log('- Data size:', res.data?.size || res.data?.length || 'unknown');
        
        // Safe preview
        try {
          const preview = typeof res.data === 'string' ? res.data.substring(0, 200) : JSON.stringify(res.data).substring(0, 200);
          console.log('- Data preview:', preview);
        } catch (e) {
          console.log('- Data preview: [Could not stringify]');
        }
      } else {
        console.log('- Error:', response.reason.message);
      }
    });
    
  } catch (error) {
    console.error('Test failed:', error);
  }
};

export const exportTournamentTeams = async (tournamentId: string): Promise<Blob> => {
  // Check if there's already a request in progress for this tournament
  if (exportCache.has(tournamentId)) {
    return exportCache.get(tournamentId)!;
  }

  const exportPromise = performExport(tournamentId);
  exportCache.set(tournamentId, exportPromise);

  try {
    const result = await exportPromise;
    return result;
  } finally {
    // Clean up cache after request completes
    exportCache.delete(tournamentId);
  }
};

const performExport = async (tournamentId: string): Promise<Blob> => {
  try {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }

    // Get the S3 URL from the API using secureApi
    const response = await secureApi.get<any>(
      `/api/v1/tournament-registration/excel/detail/${tournamentId}`,
      {
        responseType: 'json',
        timeout: 30000,
      }
    );

    // Extract S3 URL from response
    // secureApi returns response.data automatically
    let s3Url: string;
    if (typeof response === 'string') {
      s3Url = response;
    } else if (response && typeof response === 'object') {
      // Handle JSON response with data field containing the S3 URL
      s3Url = response.data || response.url || response.fileUrl || response.downloadUrl;
    } else {
      throw new Error('Server did not return a valid S3 URL');
    }

    if (!s3Url) {
      throw new Error('Server did not return a valid S3 URL');
    }

    // Try direct download approach first
    try {
      // Create a fresh axios instance without global defaults to avoid Authorization header
      // S3 presigned URLs don't need authentication headers
      const s3Axios = axios.create({
        timeout: 60000,
      });
      
      // Explicitly ensure no Authorization header is sent
      s3Axios.interceptors.request.use((config) => {
        delete config.headers['Authorization'];
        delete config.headers['authorization'];
        return config;
      });
      
      const fileResponse = await s3Axios.get(s3Url, {
        responseType: 'blob',
      });

      if (fileResponse.data && fileResponse.data.size) {
        return fileResponse.data;
      } else {
        throw new Error('S3 download returned no data');
      }
    } catch (s3Error: any) {
      // If direct download fails, try opening in new window
      window.open(s3Url, '_blank');
      
      // Return a dummy blob to prevent the error from propagating
      return new Blob(['File download initiated in new window'], { type: 'application/octet-stream' });
    }

  } catch (error: any) {
    if (error.response?.status === 401) {
      throw new Error('Authentication failed. Please login again.');
    } else if (error.response?.status === 403) {
      throw new Error('You do not have permission to export teams for this tournament.');
    } else if (error.response?.status === 404) {
      throw new Error('Tournament not found or no teams available for export.');
    } else if (error.response?.status >= 500) {
      throw new Error('Server error. Please try again later.');
    } else {
      throw new Error(error.message || 'Failed to export teams. Please try again.');
    }
  }
}; 